import React, { useState } from 'react';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';
import Button from 'components/ui/Button';

const POST_TYPES = [
{ id: 'text', label: 'Post', icon: 'FileText' },
{ id: 'question', label: 'Question', icon: 'HelpCircle' },
{ id: 'spotlight', label: 'Spotlight', icon: 'Star' },
{ id: 'milestone', label: 'Milestone', icon: 'Trophy' },
{ id: 'poll', label: 'Poll', icon: 'BarChart2' }];


export default function CreatePostModal({ onClose, onPost }) {
  const [postType, setPostType] = useState('text');
  const [content, setContent] = useState('');
  const [title, setTitle] = useState('');
  const [pollOptions, setPollOptions] = useState(['', '']);
  const [tags, setTags] = useState('');

  const handleSubmit = () => {
    if (!content?.trim()) return;
    onPost({
      type: postType,
      title,
      content,
      tags: tags?.split(',')?.map((t) => t?.trim())?.filter(Boolean),
      pollOptions: postType === 'poll' ? pollOptions?.filter(Boolean)?.map((o) => ({ label: o, pct: 0 })) : null
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(27,42,94,0.7)' }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: 'var(--color-border)' }}>
          <h2 className="font-heading font-semibold text-lg" style={{ color: 'var(--color-foreground)' }}>Create Post</h2>
          <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors">
            <Icon name="X" size={18} color="var(--color-muted-foreground)" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Author */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full overflow-hidden">
              <Image src="https://img.rocket.new/generatedImages/rocket_gen_img_1464a228c-1772710340475.png" alt="Current user profile photo of woman with dark hair smiling" className="w-full h-full object-cover" />
            </div>
            <div>
              <p className="font-caption font-semibold text-sm" style={{ color: 'var(--color-foreground)' }}>Sarah Mitchell</p>
              <p className="font-caption text-xs" style={{ color: 'var(--color-muted-foreground)' }}>Growth Member · Intermediate Investor</p>
            </div>
          </div>

          {/* Post Type Selector */}
          <div className="flex gap-2 overflow-x-auto pb-1">
            {POST_TYPES?.map((pt) =>
            <button key={pt?.id} onClick={() => setPostType(pt?.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-caption font-medium whitespace-nowrap transition-all flex-shrink-0 ${postType === pt?.id ? 'text-white' : 'border'}`}
            style={postType === pt?.id ?
            { background: 'var(--color-primary)', color: '#fff' } :
            { borderColor: 'var(--color-border)', color: 'var(--color-muted-foreground)' }}>
                <Icon name={pt?.icon} size={13} color="currentColor" />
                {pt?.label}
              </button>
            )}
          </div>

          {/* Title (optional) */}
          {(postType === 'question' || postType === 'spotlight' || postType === 'milestone') &&
          <input
            value={title}
            onChange={(e) => setTitle(e?.target?.value)}
            placeholder={postType === 'question' ? 'Your question...' : postType === 'milestone' ? 'Milestone title...' : 'Business name...'}
            className="w-full px-3 py-2 rounded-lg border text-sm font-caption outline-none focus:ring-2"
            style={{ borderColor: 'var(--color-border)', color: 'var(--color-foreground)', '--tw-ring-color': 'var(--color-accent)' }} />

          }

          {/* Content */}
          <textarea
            value={content}
            onChange={(e) => setContent(e?.target?.value)}
            placeholder={postType === 'text' ? "What's on your mind?" : postType === 'question' ? 'Provide more context...' : postType === 'milestone' ? 'Share your achievement...' : 'Tell us more...'}
            rows={4}
            className="w-full px-3 py-2 rounded-lg border text-sm font-caption outline-none focus:ring-2 resize-none"
            style={{ borderColor: 'var(--color-border)', color: 'var(--color-foreground)', '--tw-ring-color': 'var(--color-accent)' }} />
          

          {/* Poll Options */}
          {postType === 'poll' &&
          <div className="space-y-2">
              <p className="text-xs font-caption font-medium" style={{ color: 'var(--color-foreground)' }}>Poll Options</p>
              {pollOptions?.map((opt, i) =>
            <div key={i} className="flex gap-2">
                  <input
                value={opt}
                onChange={(e) => {const updated = [...pollOptions];updated[i] = e?.target?.value;setPollOptions(updated);}}
                placeholder={`Option ${i + 1}`}
                className="flex-1 px-3 py-2 rounded-lg border text-sm font-caption outline-none"
                style={{ borderColor: 'var(--color-border)', color: 'var(--color-foreground)' }} />
              
                  {pollOptions?.length > 2 &&
              <button onClick={() => setPollOptions(pollOptions?.filter((_, idx) => idx !== i))}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 transition-colors">
                      <Icon name="Minus" size={14} color="#DC2626" />
                    </button>
              }
                </div>
            )}
              {pollOptions?.length < 5 &&
            <button onClick={() => setPollOptions([...pollOptions, ''])}
            className="flex items-center gap-1.5 text-xs font-caption transition-colors"
            style={{ color: 'var(--color-accent)' }}>
                  <Icon name="Plus" size={13} color="var(--color-accent)" />
                  Add option
                </button>
            }
            </div>
          }

          {/* Tags */}
          <input
            value={tags}
            onChange={(e) => setTags(e?.target?.value)}
            placeholder="Tags (comma separated): investing, realestate..."
            className="w-full px-3 py-2 rounded-lg border text-sm font-caption outline-none"
            style={{ borderColor: 'var(--color-border)', color: 'var(--color-foreground)' }} />
          

          {/* Actions */}
          <div className="flex items-center gap-2 pt-1">
            <button className="p-2 rounded-lg hover:bg-gray-50 transition-colors" title="Add image">
              <Icon name="Image" size={18} color="var(--color-muted-foreground)" />
            </button>
            <button className="p-2 rounded-lg hover:bg-gray-50 transition-colors" title="Add link">
              <Icon name="Link" size={18} color="var(--color-muted-foreground)" />
            </button>
            <div className="flex-1" />
            <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
            <Button variant="default" size="sm" onClick={handleSubmit} disabled={!content?.trim()}>Post</Button>
          </div>
        </div>
      </div>
    </div>);

}